<template>
  <div v-if="data.employee">
    <div v-if="data.employee.email" class="mb-2">
      <div class="item__label">{{ $t(`Common.email`) }}</div>
      <div>{{ data.employee.email }}</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.phone`) }}</div>
      <div v-if="data.employee.phone">{{ data.employee.phone | phone }}</div>
      <router-link
        v-else-if="hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addPhone`) }}
      </router-link>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.company`) }}</div>
      <div v-if="data.employee.company && data.employee.company.name">
        {{ data.employee.company.name }}
      </div>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.supervisor`) }}</div>
      <div v-if="data.employee.supervisor && data.employee.supervisor.name">
        {{ data.employee.supervisor.name }}
      </div>
      <router-link
        v-else-if="hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addSupervisor`) }}
      </router-link>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.costCenter`) }}</div>
      <div v-if="isValidArray(data.costCenters)">
        <div v-if="data.costCenters.length > 4 && costCentersLimit">
          <span v-for="(item, index) in data.costCenters.slice(0, 4)" :key="index">
            <a
              v-if="hasRole('costCenter', 'ROLE_GET_COSTCENTER_ID')"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.slice(0, 4).length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.slice(0, 4).length - 1)">, </span>
            </span>
          </span>

          <div>
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.viewMore`) }}
            </a>
          </div>
        </div>

        <div v-if="data.costCenters.length <= 4 || !costCentersLimit">
          <span v-for="(item, index) in data.costCenters" :key="index">
            <a
              v-if="hasRole('costCenter', 'ROLE_GET_COSTCENTER_ID')"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.length - 1)">, </span>
            </span>
          </span>

          <div v-if="data.costCenters.length > 4">
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.seeLess`) }}
            </a>
          </div>
        </div>
      </div>

      <router-link
        v-else-if="hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCostCenter`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div class="mb-3">
      <div class="item__label">{{ $t(`Common.categories`) }}</div>

      <div v-if="isValidArray(data.employee.categoryIds)">
        <span v-for="category in data.employee.categoryIds" :key="category.value">
          <CarCategory :data="{ value: category }" tooltip />
        </span>
      </div>

      <router-link
        v-else-if="hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCategories`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.managementPlatformAccess`) }}</div>

      <div v-if="data.employee.v2Permission">
        {{ data.employee.v2Permission ? $t('Common.yes') : $t('Common.no') }}
      </div>

      <router-link
        v-else-if="hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addPlatformAccess`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div v-if="data.employee.webCallPermission || data.employee.thirdPartyWebCallPermission" class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.webcall`) }}</div>

      <div v-if="data.employee.webCallPermission && data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForEmployeesAndThirdParties`) }}
      </div>

      <div v-if="data.employee.webCallPermission && !data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForEmployees`) }}
      </div>

      <div v-if="!data.employee.webCallPermission && data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForThirdParties`) }}
      </div>

      <router-link
        v-else-if="
          !data.employee.webCallPermission &&
          !data.employee.thirdPartyWebCallPermission &&
          hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')
        "
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addWebCallPermission`) }}
      </router-link>

      <div
        v-if="
          !data.employee.webCallPermission &&
          !data.employee.thirdPartyWebCallPermission &&
          !hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID')
        "
      >
        N/A
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { hasRole } from 'utils/functions'
import { isValidArray, objectToArray } from 'corp-front-vue-commons/src/utils/functions'
import CarCategory from 'components/CarCategory'

export default {
  name: 'TabInfo',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  components: {
    CarCategory
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.employee.tabs',
      costCentersLimit: true
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isValidArray(val)
    },

    handleToggleCostCentersLimit () {
      this.costCentersLimit = !this.costCentersLimit
    },

    getCategoryData (category) {
      if (!category) return
      return objectToArray(this.$t('Common.ridesCategoriesList')).find(item => item.value === category)
    },

    change (drawer, data) {
      this.$emit('change', {
        drawer: {
          type: drawer.type,
          action: drawer.action
        },
        data: {
          id: data.id,
          name: data.name
        }
      })
    }
  }
}
</script>
